import React, {FunctionComponent, useMemo} from "react";
import {DateUtilsTypes} from "../../../../../../../../core/services/date-utils";
import {
    AugmentedDashboardWidgetEntry,
    DashboardViewFilterSectionFilters,
    DashboardWidgetData,
    DashboardWidgetEntry,
    DashboardWidgetType
} from "../../types";
import {Alert, Box, Button, Paper, Stack, Typography} from "@mui/material";
import LazyImage from "../../../../../../../components/app-specific/lazy-image";
import {DashboardTableWidget, TableConfig, TableDataPoint} from "../content/table";
import {
    BarChartConfig,
    BarChartDataPoint,
    DashboardBarChartWidget,
    DashboardLineChartWidget,
    LineChartConfig,
    LineChartDataPoint,
    TreemapDataPoint,
    TreemapWidgetConfig
} from "../content/charts";
import DashboardTreemapWidget from "../content/charts/treemap";
import InfoBlockWidget, {InfoBlockConfig, InfoBlockDataPoint} from "../content/info-block";
import LoadingLottieAnimation from "../../../../../../../components/app-specific/lottie-animations/loading";
import {DashboardWidgetConfig, FETCH_ERROR_BANNER_HEIGHT, GetMinHeightReturnValue} from "../../dashboard-widget.config";
import {SearchDataSettingsOrderBy, SearchDataSettingsPaginationInfo} from "../../../../../../../hooks/use-search";
// @ts-ignore
import NotImplementedImage from "../../assets/not-implemented.webp";
import {DashboardWidgetHeader} from "./header";
import {useTranslation} from "react-i18next";

/** Vertical padding of the visualization content Box (`padding: 1` top + bottom). */
const CONTENT_COLUMN_PADDING_Y = 16;

/**
 * Adds content-column chrome (box padding, and the fetch-error banner when present)
 * to a minHeight value used by the visualization content column.
 */
const withContentColumnChrome = (minHeight: GetMinHeightReturnValue, hasFetchError?: boolean): GetMinHeightReturnValue => {
    const extra = CONTENT_COLUMN_PADDING_Y + (hasFetchError ? FETCH_ERROR_BANNER_HEIGHT : 0);
    if (minHeight == null || extra === 0) {
        return minHeight;
    }
    if (typeof minHeight === 'number') {
        return minHeight + extra;
    }
    if (typeof minHeight === 'object' && !Array.isArray(minHeight)) {
        const adjusted: Record<string, unknown> = {};
        for (const [key, value] of Object.entries(minHeight as Record<string, unknown>)) {
            adjusted[key] = typeof value === 'number' ? value + extra : value;
        }
        return adjusted as GetMinHeightReturnValue;
    }
    return minHeight;
};

interface DashboardWidgetVisualizationProps {
    noDataAvailableOrNotImplementedYet: boolean;
    widget: AugmentedDashboardWidgetEntry;
    config: DashboardWidgetConfig;
    lastFetched: (DateUtilsTypes.Instance | null);
    refresh: () => void;
    isLoading: boolean;
    hasFetchError?: boolean;
    onDelete?: (id: DashboardWidgetEntry['connectionId']) => void;
    filters: DashboardViewFilterSectionFilters;
    isExporting?: boolean;
    exportData?: (() => Promise<void>);
    isEditMode?: boolean;
    data: DashboardWidgetData | null;
    paginationInfo?: SearchDataSettingsPaginationInfo;
    setPaginationInfo: React.Dispatch<React.SetStateAction<SearchDataSettingsPaginationInfo | undefined>>;
    orderBy?: SearchDataSettingsOrderBy;
    setOrderBy?: React.Dispatch<React.SetStateAction<SearchDataSettingsOrderBy | undefined>>;
    isIntersecting: boolean;
    toggleViewSettings?: () => void;
    hasPagination?: boolean;
    hasNavigation?: boolean;
    openEditWidgetTitleDialog: () => void;
    openEditWidgetFiltersDialog: () => void;
    canViewWidgetFilters?: boolean;
    canManageWidgetFilters?: boolean;
    simplified?: boolean;
    showLoadingIndicator: boolean;
    hasNoConfig: boolean;
}

export const DashboardWidgetVisualization: FunctionComponent<DashboardWidgetVisualizationProps> = ({
                                                                                                       noDataAvailableOrNotImplementedYet,
                                                                                                       widget,
                                                                                                       config,
                                                                                                       lastFetched,
                                                                                                       refresh,
                                                                                                       isLoading,
                                                                                                       hasFetchError = false,
                                                                                                       onDelete,
                                                                                                       filters,
                                                                                                       isExporting,
                                                                                                       exportData,
                                                                                                       isEditMode,
                                                                                                       data,
                                                                                                       paginationInfo,
                                                                                                       setPaginationInfo,
                                                                                                       orderBy,
                                                                                                       setOrderBy,
                                                                                                       isIntersecting,
                                                                                                       toggleViewSettings,
                                                                                                       hasPagination,
                                                                                                       hasNavigation,
                                                                                                       openEditWidgetFiltersDialog,
                                                                                                       openEditWidgetTitleDialog,
                                                                                                       canViewWidgetFilters,
                                                                                                       canManageWidgetFilters,
                                                                                                       simplified,
                                                                                                       showLoadingIndicator,
                                                                                                       hasNoConfig,
                                                                                                   }) => {
    const {t} = useTranslation('views', {keyPrefix: 'app-specific.dashboard-v2.widget'});

    const padding = useMemo(() => {
        switch (widget?.type) {
            case DashboardWidgetType.table:
                return 0;
            case DashboardWidgetType.lineChart:
            case DashboardWidgetType.barChart:
            case DashboardWidgetType.treeMap:
                return 2;
            case DashboardWidgetType.infoBlock:
            default:
                // TODO: Add padding for other widget types
                return 2;
        }
    }, [widget?.type]);

    const height = useMemo(() => {
        switch (widget?.type) {
            case DashboardWidgetType.table:
                return '100%';
            case DashboardWidgetType.lineChart:
            case DashboardWidgetType.barChart:
            case DashboardWidgetType.treeMap:
                return '100%';
            case DashboardWidgetType.infoBlock:
                return 'initial';
            // TODO: add for other widget types
            default:
                return '100%';
        }
    }, [widget?.type]);

    const minHeight = useMemo(() => config.getMinHeight(isLoading, simplified, noDataAvailableOrNotImplementedYet), [config, isLoading, simplified, noDataAvailableOrNotImplementedYet]);
    /**
     * Content-column minHeight includes box padding and the banner so the absolute-positioned widget face does not overflow.
     */
    const contentColumnMinHeight = useMemo(() => withContentColumnChrome(minHeight, hasFetchError), [minHeight, hasFetchError]);
    const orderByFields = useMemo(() => config.orderByFields, [config.orderByFields]);
    const chartOrderByFields = orderByFields;
    const widgetData = useMemo(() => data?.data ?? [], [data]);


    /**
     * Render content based on widget types
     */
    const content = useMemo(() => {
        const noConfig = (
            <Stack direction={'column'} alignItems={'center'} justifyContent={'center'} height={'100%'}>
                <LazyImage
                    src={NotImplementedImage}
                    onContextMenu={(e) => e.preventDefault()}
                    onDragStart={(e) => e.preventDefault()}
                    height={100 as any}
                />
                <Typography variant={'body2'} fontWeight={500} color={'text.secondary'} textAlign={'center'}>
                    No configuration available. Please wait for for a new release
                </Typography>
            </Stack>
        );

        if (hasNoConfig)
            return noConfig;

        const configVariantFinder = () => {
            return widget?.configuration?.[widget?.type]?.find(e =>
                e.variant === widget?.variant ||
                (e.variant === undefined && widget?.variant === undefined) ||
                (e.variant === null && widget?.variant === null) ||
                (e.variant === undefined && widget?.variant === null) ||
                (e.variant === null && widget?.variant === undefined)
            )?.config;
        }

        switch (widget?.type) {
            default:
                return (
                    <Stack direction={'column'} alignItems={'center'} justifyContent={'center'} height={'100%'}>
                        <LazyImage
                            src={NotImplementedImage}
                            onContextMenu={(e) => e.preventDefault()}
                            onDragStart={(e) => e.preventDefault()}
                            height={150 as any}
                        />
                        <Typography variant={'body2'} fontWeight={500} color={'text.secondary'} textAlign={'center'}>
                            {widget?.type} Not implemented yet
                        </Typography>
                    </Stack>
                );
            case DashboardWidgetType.table: {
                const config = configVariantFinder() as TableConfig | undefined;
                if (!config)
                    return noConfig;
                return (
                    <DashboardTableWidget
                        data={widgetData as TableDataPoint[]}
                        config={config}
                        identifier={widget?.id} // Specifically, use the ID of the widget instead of connectionID to allow multiple instances to use the same saved config.
                        hasPagination={hasPagination}
                        paginationInfo={paginationInfo}
                        setPaginationInfo={setPaginationInfo}
                        isLoading={isLoading}
                        isEnabled={isIntersecting}
                        minHeight={minHeight}
                        orderBy={orderBy}
                        setOrderBy={setOrderBy}
                        orderByFields={orderByFields}
                        underlyingDataVisibility={widget?.underlyingDataVisibility}
                    />
                );
            }
            case DashboardWidgetType.lineChart: {
                const config = configVariantFinder() as LineChartConfig | undefined;
                if (!config)
                    return noConfig;
                return (
                    <DashboardLineChartWidget
                        data={widgetData as LineChartDataPoint[]}
                        config={config}
                        underlyingDataVisibility={widget?.underlyingDataVisibility}
                        minHeight={minHeight}
                        isLoading={isLoading}
                        paginationInfo={paginationInfo}
                        setPaginationInfo={setPaginationInfo}
                        orderBy={orderBy}
                        setOrderBy={setOrderBy}
                        orderByFields={chartOrderByFields}
                        hasPagination={hasPagination}
                    />
                );
            }
            case DashboardWidgetType.barChart: {
                const config = configVariantFinder() as BarChartConfig | undefined;
                if (!config)
                    return noConfig;
                return (
                    <DashboardBarChartWidget
                        data={widgetData as BarChartDataPoint[]}
                        config={config}
                        underlyingDataVisibility={widget?.underlyingDataVisibility}
                        minHeight={minHeight}
                        isLoading={isLoading}
                        paginationInfo={paginationInfo}
                        setPaginationInfo={setPaginationInfo}
                        orderBy={orderBy}
                        setOrderBy={setOrderBy}
                        orderByFields={chartOrderByFields}
                        hasPagination={hasPagination}
                    />
                );
            }
            case DashboardWidgetType.treeMap:
            case DashboardWidgetType.treeMapOld: {
                const config = configVariantFinder() as TreemapWidgetConfig | undefined;
                if (!config)
                    return noConfig;
                return (
                    <DashboardTreemapWidget
                        connectionId={widget?.connectionId}
                        data={widgetData as TreemapDataPoint[]}
                        filters={filters}
                        widgetFilters={widget?.filters}
                        config={config}
                        underlyingDataVisibility={widget?.underlyingDataVisibility}
                        minHeight={minHeight}
                        isLoading={isLoading}
                        paginationInfo={paginationInfo}
                        setPaginationInfo={setPaginationInfo}
                        orderBy={orderBy}
                        setOrderBy={setOrderBy}
                        orderByFields={chartOrderByFields}
                    />
                );
            }
            case DashboardWidgetType.infoBlock: {
                const config = configVariantFinder() as InfoBlockConfig | undefined;
                if (!config)
                    return noConfig;
                return (
                    <InfoBlockWidget
                        data={widgetData as InfoBlockDataPoint[]}
                        config={config}
                        isLoading={isLoading}
                    />
                );
            }
            // TODO: Add more widget types as needed
        }
    }, [hasNoConfig, widgetData, widget?.type, widget?.configuration, widget?.variant, widget?.id, widget?.underlyingDataVisibility, widget?.connectionId, widget?.filters, hasPagination, paginationInfo, setPaginationInfo, isIntersecting, orderBy, setOrderBy, orderByFields, chartOrderByFields, minHeight, filters, isLoading]);


    return (
        <>
            <Box sx={{height: '100%', display: 'flex', flexDirection: 'column',}}>
                <DashboardWidgetHeader
                    widget={widget}
                    config={config}
                    onDelete={onDelete}
                    lastFetched={lastFetched}
                    isEditMode={isEditMode}
                    hasNoConfig={hasNoConfig}
                    simplified={simplified}
                    openEditWidgetFiltersDialog={openEditWidgetFiltersDialog}
                    canViewWidgetFilters={canViewWidgetFilters}
                    canManageWidgetFilters={canManageWidgetFilters}
                    isLoading={isLoading}
                    exportData={exportData}
                    isExporting={isExporting}
                    hasNavigation={hasNavigation}
                    toggleViewSettings={toggleViewSettings}
                    refresh={refresh}
                    openEditWidgetTitleDialog={openEditWidgetTitleDialog}
                />
                <Box sx={{
                    flexGrow: 1,
                    padding: 1,
                    position: 'relative',
                    minHeight: contentColumnMinHeight,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: hasFetchError ? 1 : 0,
                }}>
                    {
                        hasFetchError &&
                        <Alert
                            severity="error"
                            action={
                                <Button
                                    onClick={refresh}
                                    disabled={isLoading || hasNoConfig}
                                    size="small"
                                    color="inherit"
                                >
                                    Refetch
                                </Button>
                            }
                            sx={{
                                flexShrink: 0,
                                height: 48,
                                minHeight: 48,
                                '& .MuiAlert-message': {
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    py: 0.75,
                                },
                            }}
                        >
                            {t(t => t.fetchFailed)}
                        </Alert>
                    }
                    <Paper sx={{padding: padding, height: height, flex: 1, minHeight: 0}}>
                        {content}
                    </Paper>
                    {
                        showLoadingIndicator &&
                        <Box
                            sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                zIndex: 1
                            }}
                        >
                            <LoadingLottieAnimation width={'min(100%, 300px)'} className={''}/>
                        </Box>
                    }
                </Box>
            </Box>
        </>
    );
}